import {
  BaseAutoTracker,
  IAutoTrackerConfig,
  ICapturedEvent,
} from './BaseAutoTracker';
import { SelectorUtils } from './utils/SelectorUtils';
import { EventManager } from './utils/EventManager';
import { GomarkePixelConfig, PixelResponse } from '@/types';
import { AddToCartTracker } from '@/conversion/tracks/addToCart';
import type { TrackConversionFn } from '../engine';

/**
 * Configuração específica para autoAddToCart
 */
export interface IAutoAddToCartConfig extends IAutoTrackerConfig {
  extractProductData?: boolean;
  productDataSelectors?: {
    currency?: string;
    quantity?: string;
    name?: string;
    price?: string;
    category?: string;
    id?: string;
    image?: string;
  };
  customDataExtractor?: (element: Element) => Record<string, unknown>;
}

/**
 * AutoTracker para eventos de adicionar ao carrinho
 */
export class AutoAddToCartTracker extends BaseAutoTracker {
  private addToCartTracker: AddToCartTracker;
  private eventManager: EventManager;
  private autoConfig?: IAutoAddToCartConfig;
  private trackConversion?: TrackConversionFn;

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = '',
    trackConversion?: TrackConversionFn
  ) {
    super(config, sessionId, version, visitorId);
    this.trackConversion = trackConversion;
    this.addToCartTracker = new AddToCartTracker(
      config,
      sessionId,
      this.visitorId,
      version
    );
    this.eventManager = new EventManager({
      debounceMs: 300,
      throttleMs: 1000,
      enableLogging: config.debug,
    });
  }

  /**
   * Inicializa o autoTracker
   */
  public async initialize(autoConfig: IAutoAddToCartConfig): Promise<void> {
    if (!autoConfig.isEnabled) {
      this.log('AutoAddToCart desabilitado');
      return;
    }

    this.autoConfig = autoConfig;
    this.log(`Inicializando AutoAddToCart com seletor: ${autoConfig.selector}`);

    // Aguarda DOM estar pronto
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () =>
        this.setupListeners()
      );
    } else {
      await this.setupListeners();
    }

    // Observa mudanças no DOM para elementos dinâmicos
    this.observeDOM(() => this.setupListeners());

    this.isInitialized = true;
  }

  /**
   * Configura os listeners
   */
  private async setupListeners(): Promise<void> {
    if (!this.autoConfig) return;

    // Limpa listeners existentes
    this.clearListeners();

    try {
      const elements = await SelectorUtils.findElementsWithRetry(
        this.autoConfig.selector,
        this.autoConfig.selectorType,
        3,
        1000
      );

      this.log(`Encontrados ${elements.length} elementos para AddToCart`);

      elements.forEach(element => {
        // Converte Node para Element
        const htmlElement = element as Element;

        this.addListenerWithTiming(
          htmlElement,
          'click',
          this.handleAddToCart.bind(this),
          this.autoConfig!
        );

        // Adiciona listener para eventos de teclado (Enter, Space)
        this.addListenerWithTiming(
          htmlElement,
          'keydown',
          this.handleKeyboardAddToCart.bind(this),
          this.autoConfig!
        );
      });
    } catch (error) {
      this.log(`Erro ao configurar listeners: ${error}`);
    }
  }

  /**
   * Manipula clique em adicionar ao carrinho
   */
  private async handleAddToCart(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    this.log('AddToCart detectado via clique', capturedEvent);

    const productData = this.extractProductData(capturedEvent.element);

    // Garantir que temos valores válidos para campos obrigatórios
    const itemId =
      productData.id ||
      this.generateItemId(capturedEvent.element) ||
      `item_${Date.now()}`;
    const itemName =
      productData.name ||
      this.extractTextContent(capturedEvent.element) ||
      'Produto';

    if (!itemId || !itemName) {
      this.log('Erro: itemId ou itemName não encontrados', {
        itemId,
        itemName,
        productData,
      });
      return Promise.resolve({
        success: false,
        error: 'Dados do produto não encontrados',
        timestamp: Date.now(),
      });
    }

    const payload = {
      itemId,
      itemName,
      value: productData.price,
      currency: productData.currency || 'BRL',
      category: productData.category,
      quantity: productData.quantity || 1,
    };
    if (this.trackConversion) {
      return this.trackConversion('addToCart', payload);
    }
    return this.addToCartTracker.track(payload);
  }

  /**
   * Manipula teclas de adicionar ao carrinho
   */
  private async handleKeyboardAddToCart(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    const keyboardEvent = capturedEvent.event as KeyboardEvent;

    // Só processa Enter e Space
    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') {
      return Promise.resolve({
        success: false,
        error: 'Tecla não suportada',
        timestamp: Date.now(),
      });
    }

    this.log('AddToCart detectado via teclado', capturedEvent);
    return this.handleAddToCart(capturedEvent);
  }

  /**
   * Extrai dados do produto do elemento
   */
  private extractProductData(element: Element): {
    id?: string;
    name?: string;
    price?: number;
    category?: string;
    quantity?: number;
    image?: string;
    currency?: string;
  } {
    if (!this.autoConfig?.productDataSelectors) {
      return {};
    }

    const data: Record<string, unknown> = {};

    if (this.autoConfig.productDataSelectors.id) {
      data.id = element.getAttribute(this.autoConfig.productDataSelectors.id);
    }
    if (this.autoConfig.productDataSelectors.name) {
      data.name = element.getAttribute(
        this.autoConfig.productDataSelectors.name
      );
    }
    if (this.autoConfig.productDataSelectors.price) {
      data.price = this.parsePrice(
        element.getAttribute(this.autoConfig.productDataSelectors.price) ??
          undefined
      );
    }
    if (this.autoConfig.productDataSelectors.category) {
      data.category = element.getAttribute(
        this.autoConfig.productDataSelectors.category
      );
    }
    if (this.autoConfig.productDataSelectors.image) {
      data.image = element.getAttribute(
        this.autoConfig.productDataSelectors.image
      );
    }
    if (this.autoConfig.productDataSelectors.quantity) {
      data.quantity = element.getAttribute(
        this.autoConfig.productDataSelectors.quantity
      );
    }
    if (this.autoConfig.productDataSelectors.currency) {
      data.currency = element.getAttribute(
        this.autoConfig.productDataSelectors.currency
      );
    }

    if (this.autoConfig.customDataExtractor) {
      const customData = this.autoConfig.customDataExtractor(element);
      Object.assign(data, customData);
    }

    return data;
  }

  /**
   * Gera ID único para o item
   */
  private generateItemId(element: Element): string {
    const position = SelectorUtils.getElementPosition(element);
    return `item_${Date.now()}_${Math.floor(position.top)}_${Math.floor(position.left)}`;
  }

  /**
   * Extrai texto do elemento
   */
  private extractTextContent(element: Element): string {
    return (
      element.textContent?.trim() || element.getAttribute('title') || 'Produto'
    );
  }

  /**
   * Converte string de preço para número
   */
  private parsePrice(priceText?: string): number | undefined {
    if (!priceText) return undefined;

    // Remove caracteres não numéricos exceto vírgula e ponto
    const cleanPrice = priceText.replace(/[^\d,.-]/g, '');

    // Converte vírgula para ponto se necessário
    const normalizedPrice = cleanPrice.replace(',', '.');

    const price = parseFloat(normalizedPrice);
    return isNaN(price) ? undefined : price;
  }

  /**
   * Para o autoTracker
   */
  public stop(): void {
    this.clearListeners();
    this.eventManager.clear();
    this.isInitialized = false;
    this.log('AutoAddToCart parado');
  }

  /**
   * Reconstrói os listeners
   */
  public async rebuild(): Promise<void> {
    if (this.autoConfig) {
      await this.setupListeners();
    }
  }

  /**
   * Obtém estatísticas específicas
   */
  public getAddToCartStats(): {
    baseStats: unknown;
    eventStats: unknown;
  } {
    return {
      baseStats: {},
      eventStats: this.eventManager.getStats(),
    };
  }

  /**
   * Log de eventos
   */
  private log(message: string, data?: unknown): void {
    if (this.baseConfig.debug) {
      console.log(`[AutoAddToCart] ${message}`, data);
    }
  }
}

// Função de conveniência para compatibilidade
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
export const autoAddToCart = (_config: IAutoAddToCartConfig): void => {
  // Esta função será implementada quando integrarmos com a classe principal
  console.warn('autoAddToCart: Use a classe AutoAddToCartTracker diretamente');
};
